import React, { useState, memo } from "react";
import { View, Text, Image, TouchableOpacity } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import Separator from "../Separator";
import { AppImages } from "../../utility/AppImages";
import { CommonButton } from "../../common/CommonButton";
import { DisconnectUser } from "../../services/stream";
import { toast } from "react-toastify";
import StreamingPlateformComponentSkeleton from "../skeleton-components/StreamingPlateformComponentSkeleton";

const StreamingPlateformComponent = ({
  fetchStatus,
  accessToken,
  socialMediaPlatforms,
  streamingTooltip,
  setStreamingTooltip,
  platformStatus,
  redirectToYouTubeOAuth,
  redirectToTwitchOAuth,
  isLoading = false,
  twitchUsername,
  youtubeUsername,
  squadifiUsername,
  loadingState,
}) => {
  const [disconnectLoading, setDisconnectLoading] = useState({
    YouTube: false,
    Twitch: false,
  });
  const deviceSize = useDeviceSize();
  const styles = useStyles();


  const disConnect = async (selectedPlatform: string) => {
    try {
      setDisconnectLoading(prev => ({ ...prev, [selectedPlatform]: true }));
      const response = await DisconnectUser(accessToken, selectedPlatform);
      if (response.statusCode === 200) {
        toast.success(response.body.message);
        fetchStatus(true);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDisconnectLoading(prev => ({ ...prev, [selectedPlatform]: false }));
    }
  };

  const isMobile = deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;

  if (isLoading) {
    return <StreamingPlateformComponentSkeleton />;
  }

  // Common content renderer
  const renderContent = () => (
    <>
      <Text
        style={[
          isMobile
            ? {
              color: "#FFF",
              fontSize: 16,
              fontWeight: "800",
              lineHeight: 16 * 1.155, // 115.5% line-height
            }
            : AppFontStyle.BOLD_ANYBODY_24,
        ]}
      >
        {isMobile ? "Connect Streaming platforms" : "Streaming Platform Integration"}
      </Text>
      <Separator
        color={isMobile ? "#374151" : AppColors.black_2D2733}
        height={1}
        marginTop={isMobile ? 0 : 12}
        marginBottom={isMobile ? 0 : 12}
        marginHorizontal={0}
      />
      <Text
        style={[
          isMobile
            ? {
              color: "#C4C4C4",
              fontSize: 12,
              fontWeight: "400",
              lineHeight: 12 * 1.355, // 135.5% line-height
            }
            : {
              ...AppFontStyle.REGULAR_ANYBODY_14,
              color: AppColors.white,
            },
        ]}
      >
        Connect your preferred streaming platform to broadcast your event
        live.
      </Text>
      {socialMediaPlatforms.map((platformInfo) => {
        const platform = platformInfo.label;
        const isConnected = platformStatus[platform] === "Connected";
        const isConnecting = loadingState?.[platform] || false;
        const username =
          platform === "Twitch" ? twitchUsername : youtubeUsername;

        return (
          <View
            key={platform}
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              paddingTop: 12,
              alignItems: "center",
              width: "100%",
            }}
          >
            <View
              style={{
                flexDirection: "row",
                gap: 12,
                alignItems: "center",
                flex: 1,
              }}
            >
              <Image
                source={
                  platform === "Twitch"
                    ? AppImages.icTwitchLogo
                    : AppImages.icYoutubeLogo
                }
                style={{
                  width: isMobile ? 30 : 56,
                  height: isMobile ? 30 : 56,
                }}
                resizeMode="contain"
              />
              <View
                style={{
                  flexDirection: "column",
                  gap: 4,
                  width: isMobile ? 80 : 150,
                }}
              >
                <Image
                  source={
                    platform === "Twitch"
                      ? AppImages.icTwitchTextLogo
                      : AppImages.icYoutubeStreaming
                  }
                  style={{
                    width: isMobile
                      ? (platform === "Twitch" ? 58.5 : 64)
                      : 84,
                    height: isMobile
                      ? (platform === "Twitch" ? 19.5 : 17)
                      : 28,
                  }}
                  resizeMode="contain"
                />
                {username && isConnected && (
                  <Text
                    style={[
                      isMobile
                        ? {
                          color: "#C4C4C4",
                          fontSize: 12,
                          fontWeight: "400",
                          lineHeight: 12 * 1.355, // 135.5% line-height
                          flexWrap: "wrap",
                        }
                        : {
                          ...AppFontStyle.REGULAR_ANYBODY_14,
                          color: AppColors.white,
                          flexWrap: "wrap",
                        },
                    ]}
                    numberOfLines={2}
                    ellipsizeMode="tail"
                  >
                    {username}
                  </Text>
                )}
              </View>
            </View>
            {/* Right side: Button + Delete */}
            <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
              <CommonButton
                title={isConnecting ? "Connecting..." : isConnected ? "Connected" : "Connect"}
                onTap={() => {
                  if (!isConnected && !isConnecting) {
                    if (platform === "YouTube") {
                      redirectToYouTubeOAuth();
                    } else if (platform === "Twitch") {
                      redirectToTwitchOAuth();
                    }
                  }
                }}
                buttonStyle={{
                  paddingVertical: 10,
                  paddingHorizontal: 14,
                  borderRadius: 8,
                  shadowOpacity: 0,
                  borderWidth: 2,
                  borderColor: isConnected ? AppColors.gray_5C5C5C : AppColors.appViolentColor_6D08C3,
                  backgroundColor: isConnected ? AppColors.purple_453B50 : AppColors.appViolentColor_6D08C3,
                  opacity: isConnecting ? 0.7 : 1,
                }}
                fontStyle={[
                  isMobile
                    ? AppFontStyle.SEMIBOLD_ANYBODY_12
                    : AppFontStyle.SEMIBOLD_ANYBODY_16,
                  { color: isConnected ? AppColors.appGreenColor_51AF07 : AppColors.white },
                ]}
                image={isConnected}
                imageUrl={isConnected ? AppImages.icCheckedGreen : undefined}
                imageStyle={{ width: 20, height: 20 }}
                disabled={isConnecting}
              />
              {isConnected && (
                <TouchableOpacity
                  onPress={() => disConnect(platform)}
                  disabled={disconnectLoading[platform]}
                  style={{ opacity: disconnectLoading[platform] ? 0.5 : 1 }}
                >
                  {disconnectLoading[platform] ? (
                    <View style={{
                      paddingHorizontal: 8,
                      paddingVertical: 4,
                      backgroundColor: AppColors.gray_5C5C5C,
                      borderRadius: 4,
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      <Text style={{
                        color: AppColors.white,
                        fontSize: isMobile ? 10 : 12,
                        fontWeight: '500'
                      }}>
                        Disconnecting...
                      </Text>
                    </View>
                  ) : (
                    <Image
                      source={AppImages.icRedDelete}
                      style={{
                        width: 24,
                        height: 24,
                        resizeMode: "contain",
                      }}
                    />
                  )}
                </TouchableOpacity>
              )}
            </View>
          </View>
        );
      })}
    </>
  );

  return (
    <View style={{ justifyContent: "space-between", flexDirection: "row" }}>
      {isMobile ? (
        <LinearGradient
          colors={["rgba(31, 41, 55, 0.5)", "rgba(17, 24, 39, 0.5)"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{
            padding: 8,
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 6,
            alignSelf: "stretch",
            borderRadius: 8,
            borderWidth: 0.8,
            borderColor: "#374151",
            width: "100%",
          }}
        >
          {renderContent()}
        </LinearGradient>
      ) : (
        <View
          style={{
            backgroundColor: AppColors.purple_453B50,
            borderRadius: 10,
            padding: 24,
            width: "68%",
          }}
        >
          {renderContent()}
        </View>
      )}
      {!isMobile && streamingTooltip && (
        <View
          style={{
            width: "30%",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <Image
            source={AppImages.icTriangle}
            style={{ width: 32, height: 30, left: -20, bottom: -35 }}
            resizeMode="contain"
          />
          <View
            style={{
              backgroundColor: AppColors.purple_453B50,
              borderRadius: 10,
              padding: 12,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <Text style={[AppFontStyle.SEMIBOLD_ANYBODY_16, {}]}>
                How to Connect
              </Text>
              <TouchableOpacity onPress={() => setStreamingTooltip(false)}>
                <Image
                  source={AppImages.icCloseWhiteSvg}
                  style={{
                    width: 24,
                    height: 24,
                  }}
                  resizeMode="contain"
                />
              </TouchableOpacity>
            </View>
            <View style={{ flexDirection: "row", gap: 5 }}>
              <Text style={styles.tooltipText}>1.</Text>
              <Text style={styles.tooltipText}>
                Click the Connect button next to your preferred platform.
              </Text>
            </View>
            <View style={{ flexDirection: "row", gap: 5 }}>
              <Text style={styles.tooltipText}>2.</Text>
              <Text style={styles.tooltipText}>
                Log in and authorize Kallout to access your account.
              </Text>
            </View>
            <View style={{ flexDirection: "row", gap: 5 }}>
              <Text style={styles.tooltipText}>3.</Text>
              <Text style={styles.tooltipText}>
                Once connected, you'll see a green checkmark confirming the
                integration.
              </Text>
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

export default StreamingPlateformComponent;

const useStyles = CreateResponsiveStyle(
  {
    tooltipText: {
      ...AppFontStyle.REGULAR_ANYBODY_14,
      marginBottom: 5,
      color: AppColors.gray_C4C4C4,
    },
  },
  {
    [DEVICE_SIZES.LG]: {},
    [DEVICE_SIZES.MD]: {},
    [DEVICE_SIZES.SM]: {},
    [DEVICE_SIZES.XS]: {},
  }
);
